import React, { memo, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { StyleSheet, css } from 'aphrodite';
import { NavLink } from 'react-router-dom';
import { ArrowDropDown } from '@material-ui/icons';
import { Button } from '@material-ui/core';
import { isValidString } from '../../../../catalog/Validity';
import NavItemsDesktopWithDropDown from './NavItemsDesktopWithDropDown';
import { context } from '../../../../globalState/GlobalState';
import { getNavLinkTargetValue } from '../../../../catalog/Website';
import NavItemCart from './NavItemCart';
import NavItemsMore from './NavItemsMore';
import { optimXStoreWebsite } from '../../../../catalog/TeamInfo';

const NavItemsDesktop = (props) => {
    const { navItems } = props;
    const { globalState } = useContext(context);

    const styles = StyleSheet.create({
        navitem: {
            color: isValidString(globalState.websiteStyles.colors.fontColor) ? `${globalState.websiteStyles.colors.fontColor} !important` : '#ffffff !important',
        },
    });

    // One open dropdown at a time and one stable ref object per item. Hooks must not be called inside navItems.map:
    // the item count can change between renders (team data loads, store links appear), which shifts every later hook
    // and crashes the page (React error 311).
    const [openIndex, setOpenIndex] = useState(-1);
    const anchorRefList = useRef([]);
    const getAnchorRef = (index) => {
        if (!anchorRefList.current[index]) {
            anchorRefList.current[index] = { current: null };
        }
        return anchorRefList.current[index];
    };
    const setMenuOpenFor = (index) => {
        return (value) => {
            setOpenIndex((previous) => {
                const open = typeof value === 'function' ? value(previous === index) : value;
                if (open) {
                    return index;
                }
                return previous === index ? -1 : previous;
            });
        };
    };

    // Items that don't fit between the logo and the right edge move into "More". Every item stays rendered (each
    // owns its dropdown state); overflowing ones are hidden from the row and listed in NavItemsMore instead.
    const MORE_WIDTH = 120;
    const containerRef = useRef(null);
    const cartRef = useRef(null);
    const slotRefs = useRef([]);
    const widthsRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(navItems.length);
    // Bumped to force a re-render (and so a re-measure) even when visibleCount is already the full count.
    const [, setMeasureTick] = useState(0);
    const remeasure = () => {
        widthsRef.current = null;
        setVisibleCount(navItems.length);
        setMeasureTick((tick) => { return tick + 1; });
    };
    const itemsKey = navItems.map((item) => { return item.title; }).join('|');

    const computeVisible = useCallback(() => {
        const el = containerRef.current;
        if (!el || el.offsetParent === null || !widthsRef.current) {
            return;
        }
        const row = el.closest('.navbar-navitems') || el.closest('.nav-items-container') || el.parentElement;
        const rowRect = row.getBoundingClientRect();
        let { left } = rowRect;
        const header = el.closest('.navbar-navbar') || el.closest('nav') || document;
        const logo = header.querySelector('.icon-container');
        if (logo && logo.offsetParent !== null) {
            const logoRect = logo.getBoundingClientRect();
            const overlaps = logoRect.bottom > rowRect.top && logoRect.top < rowRect.bottom;
            if (overlaps && logoRect.right > left) {
                left = logoRect.right + 16;
            }
        }
        const cartWidth = cartRef.current ? cartRef.current.getBoundingClientRect().width : 0;
        const available = rowRect.right - left - cartWidth;
        const widths = widthsRef.current;
        const total = widths.reduce((sum, width) => { return sum + width; }, 0);
        if (total <= available) {
            setVisibleCount(widths.length);
            return;
        }
        let used = MORE_WIDTH;
        let count = 0;
        while (count < widths.length && used + widths[count] <= available) {
            used += widths[count];
            count += 1;
        }
        setVisibleCount(count);
    }, []);

    // Menu changed: show everything once, measure each item, then fit.
    useEffect(() => {
        remeasure();
    }, [itemsKey]);

    useLayoutEffect(() => {
        if (widthsRef.current === null && visibleCount === navItems.length && containerRef.current && containerRef.current.offsetParent !== null) {
            widthsRef.current = navItems.map((item, index) => {
                const slot = slotRefs.current[index];
                return slot ? slot.getBoundingClientRect().width : 0;
            });
            computeVisible();
        }
    });

    useEffect(() => {
        // Item widths change across breakpoints (smaller menu font below 1440px), so re-measure after resizing.
        // Measuring happens in a layout effect, before paint, so the full row never flashes on screen.
        let timer = null;
        const onResize = () => {
            clearTimeout(timer);
            timer = setTimeout(remeasure, 120);
        };
        window.addEventListener('resize', onResize);
        // Fonts load after first paint and change item widths.
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(remeasure).catch(() => {});
        }
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', onResize);
        };
    }, [itemsKey]);

    const getNavItem = (item, index) => {
        if (item.value === '/options') {
            return (
                <Button
                    className={`nav-item ${css(styles.navitem)}`}
                    onMouseOver={() => { setMenuOpenFor(index)(true); }}
                    onMouseLeave={() => { setMenuOpenFor(index)(false); }}
                    type="button"
                    ref={getAnchorRef(index)}
                >
                    {item.title}
                    <ArrowDropDown style={{ fontSize: '20px' }} />
                    <NavItemsDesktopWithDropDown options={item.options} menuOpen={openIndex === index} setMenuOpen={setMenuOpenFor(index)} anchorRef={getAnchorRef(index)} />
                </Button>
            );
        } if (item.value === '/custom') {
            return (
                <a className={`nav-item m-t-0 m-b-0 ${css(styles.navitem)}`} href={item.link} target={getNavLinkTargetValue(item.openNewTab, false)} rel="noreferrer noopener">
                    {item.title}
                </a>
            );
        } if (item.value === '/custompage' || item.value === '/custompages') {
            return (
                <NavLink className={`nav-item ${css(styles.navitem)}`} to={`/pages/${item.link}`} target={getNavLinkTargetValue(item.openNewTab, true)}>
                    <p className="m-t-0 m-b-0">
                        {item.title}
                    </p>
                </NavLink>
            );
        } if (item.value === '/forms') {
            return (
                <NavLink className={`nav-item ${css(styles.navitem)}`} to={`/forms/${item.link}`} target={getNavLinkTargetValue(item.openNewTab, true)}>
                    <p className="m-t-0 m-b-0">
                        {item.title}
                    </p>
                </NavLink>
            );
        }
        return (
            <NavLink className={`nav-item ${css(styles.navitem)}`} to={item.link} target={getNavLinkTargetValue(item.openNewTab, true)}>
                <p className="m-t-0 m-b-0">
                    {item.title}
                </p>
            </NavLink>
        );
    };

    return (
        <div className="nav-items-normal" ref={containerRef}>
            {!optimXStoreWebsite ? (
                <>
                    {navItems.map((item, index) => {
                        return (
                            <span
                                key={item.title}
                                className="nav-item-slot"
                                ref={(node) => { slotRefs.current[index] = node; }}
                                style={index < visibleCount ? undefined : { display: 'none' }}
                            >
                                {getNavItem(item, index)}
                            </span>
                        );
                    })}
                    {visibleCount < navItems.length ? <NavItemsMore items={navItems.slice(visibleCount)} navItemClassName={css(styles.navitem)} /> : null}
                </>
            ) : null}
            {isValidString(globalState.stripeAccountId) ? <span className="nav-item-slot" ref={cartRef}><NavItemCart desktop /></span> : null}
        </div>
    );
};

NavItemsDesktop.propTypes = {
    navItems: PropTypes.array.isRequired,
};

export default (memo(NavItemsDesktop));
